import { CombinedError } from '@urql/core';

import { useAnalyticsService } from '~/composables/core';
import { useLeaveForSchoolAccount } from '~/composables/unified/useLeaveForSchoolAccount';
import { useLessonShareCode } from '~/composables/unified/useLessonShareCode';
import { AuthAnalyticsMethod } from '~/constants/auth';
import { EVENTS } from '~/constants/events';
import { MARKETING_WORDING_VERSION } from '~/constants/marketingConsent';
import { UNIFIED_ROUTE_LOGIN } from '~/constants/routes/unified';
import { LoginFlowType, SiteRoleType } from '~/graphql/generated/graphql';
import { loginResumeQuery, loginResumeStepFor } from '~/types/login';
import { computeDurationSeconds } from '~/utils/unified/computeDurationSeconds';
import { parseEntryTrackingFromQuery } from '~/utils/unified/entryTracking';
import { isSchoolAccountEmailError } from '~/utils/unified/schoolAccountEmail';
import { parseSelectedEntityFromQuery } from '~/utils/unified/selectedEntity';
import { readUtmKeyFromQuery } from '~/utils/unified/signupLink';

interface VerifyArgs {
  email: string;
  code: string;
  /** Runs before /login or the school account page opens, while the caller is still mounted. */
  onHandOff?: () => void;
}

interface RegisterArgs {
  email: string;
  firstName?: string;
  lastName?: string;
  /** YYYY-MM */
  birthMonthYear?: string;
  stateId?: string;
  /** Null when the marketing box was not shown. */
  marketingOptIn?: boolean | null;
  /** Runs before the school account page opens, while the caller is still mounted. */
  onHandOff?: () => void;
}

type LoginKind = 'existingRole' | 'matchedInvite' | 'signUp';

interface VerifyResult {
  isNewRole: boolean;
  isMatchedInvite: boolean;
  error: string | null;
  /** The code was right but the sign-in still owes a step this flow does not have, so /login took over. */
  handedOff?: boolean;
  /** Set when the flow has navigated away, so the step has nothing left to show or advance. */
  redirected?: true;
}

interface RegisterResult {
  error: string | null;
  /** Single-use token for a first password; null when sign-up did not offer one. */
  passwordChangeToken: string | null;
  /** Set when the flow has navigated away, so the step has nothing left to show or advance. */
  redirected?: true;
}

export const useOnboardingLogin = () => {
  const { t } = useI18n();
  const route = useRoute();
  const { verifyLoginCode, signUpStudent, completeLoginProcess, isGhostStudent } = useAuth();
  const { clear, isManageReferred } = useCrossSitesConfig();
  const analytics = useAnalyticsService();
  const { shareCode } = useLessonShareCode();
  const startTime = Date.now();

  // Verifying a code consumes it server-side, and signing up creates the account. These latches let
  // a retry resume from whichever later step failed instead of repeating a one-shot side effect —
  // re-verifying a consumed code would otherwise surface a misleading "invalid code" error.
  const verified = ref(false);
  const isNewRole = ref(false);
  const matchedInvite = ref(false);
  const signedUp = ref(false);
  const passwordChangeToken = ref<string | null>(null);

  // Logging in the real account ends the ghost session, so whether this student was a ghost is read
  // once, before the first step that replaces the session, and kept for the events that follow.
  const wasGhostUser = ref<boolean | null>(null);
  const captureGhostStatus = () => {
    if (wasGhostUser.value === null) {
      wasGhostUser.value = isGhostStudent.value ?? false;
    }
  };

  // `signedUp` only latches once the mutation has come back, so it cannot stop a second submit sent
  // underneath the first — and two signups racing each other race the session the first one
  // regenerates, so the loser is refused for a role context that is no longer where it left it. The
  // student sees a dead-end on an account that was in fact created. One at a time.
  //
  // The second caller waits on the first one's promise rather than being told nothing went wrong:
  // an answer invented mid-flight is a success the registration has not reached, and the step it
  // advances is the one the failure was supposed to keep the student on.
  let inFlightRegistration: Promise<RegisterResult> | null = null;

  const trackLoginCompleted = (kind: LoginKind) => {
    const { entrySource, entryCta } = parseEntryTrackingFromQuery(route.query);
    const method = AuthAnalyticsMethod.EmailOtp;

    if (kind === 'existingRole') {
      analytics.track(EVENTS.UNIFIED_AUTH_LOGIN_COMPLETED, {
        method,
        roleType: SiteRoleType.Student,
        isNewUser: false,
        wasGhostUser: wasGhostUser.value ?? false,
        durationSeconds: computeDurationSeconds(startTime, { maxSeconds: 3600 }),
        entrySource,
        entryCta,
      });
      return;
    }

    // Legacy registration handlers fire this on completion; the onboarding flow registers new
    // students at the terms step (or at the OTP step for a matched invite), so mirror it there to
    // keep the funnel intact.
    analytics.track(EVENTS.ACCOUNT_REGISTER_COMPLETED);
    if (kind === 'matchedInvite') {
      analytics.track(EVENTS.UNIFIED_AUTH_AUTO_SIGNUP_COMPLETED, { method, entrySource, entryCta });
    }
    if (wasGhostUser.value) {
      analytics.track(EVENTS.UNIFIED_AUTH_GHOST_CONVERTED, { entrySource, entryCta });
    }
  };

  const leaveForSchoolAccountPage = useLeaveForSchoolAccount();
  const leaveForSchoolAccount = () => leaveForSchoolAccountPage({
    roleType: SiteRoleType.Student,
    method: AuthAnalyticsMethod.EmailOtp,
    ...parseEntryTrackingFromQuery(route.query),
  });

  const completeLogin = async (kind: LoginKind): Promise<{ error: string | null }> => {
    captureGhostStatus();

    try {
      await completeLoginProcess();
    }
    catch {
      return { error: t('general.unexpectedError') };
    }

    clear();
    trackLoginCompleted(kind);

    return { error: null };
  };

  // OTP step: verify the code and, for an existing role, log the user in. A new role's account is
  // NOT created here — registration is deferred to the terms step so no account exists until the
  // user consents (see registerAndLogin). `selectedEntity` is forwarded so existing users hitting
  // the onboarding flow land with the same wallet entity context that signup seeds.
  const verifyLogin = async ({ email, code, onHandOff }: VerifyArgs): Promise<VerifyResult> => {
    if (!verified.value) {
      const selectedEntity = parseSelectedEntityFromQuery(route.query);
      const { flow, hasMatchedInvite, error } = await verifyLoginCode({
        roleType: SiteRoleType.Student,
        email,
        code,
        ...(selectedEntity ? { selectedEntity } : {}),
      });

      if (isSchoolAccountEmailError(error)) {
        onHandOff?.();
        await leaveForSchoolAccount();
        return { isNewRole: false, isMatchedInvite: false, error: null, redirected: true };
      }

      if (!flow || flow === LoginFlowType.InvalidCode) {
        const message = error instanceof CombinedError
          ? getFirstErrorMessageFromCombinedErrors(t, error)
          : (typeof error === 'string' ? error : t('general.auth.invalidOtpCode'));
        return { isNewRole: false, isMatchedInvite: false, error: message };
      }

      // The server has parked this sign-in on its password or its choice of school account; the
      // /login card finishes it, keeping the selected entity and redirect so the lesson lane survives.
      const resumeStep = loginResumeStepFor(flow);
      if (resumeStep) {
        const query = Object.fromEntries(Object.entries(route.query).filter(([key]) => key !== 'code'));
        onHandOff?.();
        await navigateTo({ path: UNIFIED_ROUTE_LOGIN, query: { ...query, ...loginResumeQuery({ email, step: resumeStep }) } });
        return { isNewRole: false, isMatchedInvite: false, error: null, handedOff: true };
      }

      verified.value = true;
      // A matched invite provisions the account inside the verify itself, so there is nothing left
      // to sign up: it logs in like an existing role but is reported as a new user's sign-up.
      matchedInvite.value = flow === LoginFlowType.NewRole && hasMatchedInvite;
      isNewRole.value = flow === LoginFlowType.NewRole && !hasMatchedInvite;
    }

    // New roles have no account yet; advance to the terms step where they consent and register.
    if (isNewRole.value) {
      return { isNewRole: true, isMatchedInvite: false, error: null };
    }

    const { error } = await completeLogin(matchedInvite.value ? 'matchedInvite' : 'existingRole');
    return { isNewRole: false, isMatchedInvite: matchedInvite.value, error };
  };

  // Terms step (new roles only): now that the user has consented, create the student + role and log
  // them in. Carries `selectedEntity`, the entry-tracking params and the campaign key through so
  // EXPLORE_CONTENT and campaign signups land with the wallet entity + lane set (drives the
  // post-signup dashboard activity and the coach's opening starters), and the referral code, which
  // the server credits to the referrer only from this mutation.
  const runRegistration = async (
    { email, firstName, lastName, birthMonthYear, stateId, marketingOptIn, onHandOff }: RegisterArgs,
  ): Promise<RegisterResult> => {
    if (!signedUp.value) {
      const selectedEntity = parseSelectedEntityFromQuery(route.query);
      const { entrySource, entryCta } = parseEntryTrackingFromQuery(route.query);
      const utmKey = readUtmKeyFromQuery(route.query);
      const referralCode = typeof route.query.referralCode === 'string' ? route.query.referralCode : '';

      captureGhostStatus();
      const { error: signUpError, passwordChangeToken: token } = await signUpStudent({
        email,
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
        ...(birthMonthYear ? { birthMonthYear } : {}),
        ...(stateId ? { stateId } : {}),
        ...(marketingOptIn !== null && marketingOptIn !== undefined
          ? { marketingOptIn, marketingWordingVersion: MARKETING_WORDING_VERSION }
          : {}),
        ...(shareCode.value ? { lessonShareCode: shareCode.value } : {}),
        ...(selectedEntity ? { selectedEntity } : {}),
        ...(entrySource ? { entrySource } : {}),
        ...(entryCta ? { entryCta } : {}),
        ...(utmKey ? { utmKey } : {}),
        ...(referralCode ? { referralCode } : {}),
        ...(isManageReferred.value ? { isManageReferred: true } : {}),
      });
      if (isSchoolAccountEmailError(signUpError)) {
        onHandOff?.();
        await leaveForSchoolAccount();
        return { error: null, passwordChangeToken: null, redirected: true };
      }
      if (signUpError) {
        return { error: getFirstErrorMessageFromCombinedErrors(t, signUpError), passwordChangeToken: null };
      }
      signedUp.value = true;
      passwordChangeToken.value = token ?? null;
    }

    const { error } = await completeLogin('signUp');
    return { error, passwordChangeToken: error ? null : passwordChangeToken.value };
  };

  const registerAndLogin = (
    args: RegisterArgs,
  ): Promise<RegisterResult> => {
    if (inFlightRegistration) {
      return inFlightRegistration;
    }

    const run = runRegistration(args).finally(() => {
      inFlightRegistration = null;
    });
    inFlightRegistration = run;

    return run;
  };

  // "Wrong email?" starts the student over with another address, so the latches that let a retry skip
  // a verify already spent must not carry the old address's verify onto the new one.
  const reset = () => {
    verified.value = false;
    isNewRole.value = false;
    matchedInvite.value = false;
    signedUp.value = false;
    passwordChangeToken.value = null;
  };

  return { verifyLogin, registerAndLogin, reset };
};
